import { readCsrfToken } from './auth';

/**
 * Spec 003 — session helpers. `getMe` fetches the current identity and silently refreshes once on a 401
 * (transparent access-token renewal, SC-017). `logout` / `logoutAll` end the session. All send cookies;
 * mutations echo the CSRF token.
 */

export interface Me {
  id: string;
  email: string;
  role: 'member' | 'board' | 'admin';
}

export type MeResult = { kind: 'ok'; user: Me } | { kind: 'unauthenticated' } | { kind: 'error' };

async function fetchMe(): Promise<Response> {
  return fetch('/api/me', { credentials: 'include', headers: { accept: 'application/json' } });
}

async function tryRefresh(): Promise<boolean> {
  try {
    const headers: Record<string, string> = { accept: 'application/json' };
    const token = readCsrfToken();
    if (token) headers['x-csrf-token'] = token;
    const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include', headers });
    return res.ok;
  } catch {
    return false;
  }
}

export async function getMe(): Promise<MeResult> {
  try {
    let res = await fetchMe();
    if (res.status === 401 && (await tryRefresh())) {
      res = await fetchMe(); // retry once after a successful silent refresh
    }
    if (res.ok) return { kind: 'ok', user: (await res.json()) as Me };
    if (res.status === 401) return { kind: 'unauthenticated' };
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

async function postCsrf(path: string): Promise<boolean> {
  try {
    const headers: Record<string, string> = { accept: 'application/json' };
    const token = readCsrfToken();
    if (token) headers['x-csrf-token'] = token;
    const res = await fetch(path, { method: 'POST', credentials: 'include', headers });
    return res.ok;
  } catch {
    return false;
  }
}

export const logout = (): Promise<boolean> => postCsrf('/api/auth/logout');
export const logoutEverywhere = (): Promise<boolean> => postCsrf('/api/auth/logout-all');
